// App.js - Fixed version with proper ModalProvider usage
import EXIF from 'exif-js';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  DashboardLayout,
  Layout,
  UserDataLayout,
  CalendarLayout,
} from "./layouts";

import "./App.scss";
import "./fonts.css";

import { useEffect, useState } from "react";
import { LanguageProvider, ModalProvider, useModal } from "context";
import { ConsentProvider } from "context/ConsentContext";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectCurrentUser, selectUserType } from "store/auth/selectors";
import GTM from "GTM";
import ConsentBanner from "reusableComponents/ConsentBanner";

import {
  ContactsPage,
  ContactOrganiserPage,
  FaqPage,
  StaticPage,
  SearchesPage,
  LandingPage,
  SpacesListPage,
  HomePage,
  CustomPage,
  NotFoundPage,
  ResetPasswordPage,
  SpacePage,
  BasketPage,
  FavoritesPage,
  DashboardMainPage,
  MessagesPage,
  PasswordAndEmailEditingPage,
  BookingsListPage,
  BookingsHistory,
  PaymentMethods,
  RatingsPage,
  BookingInvoices,
  CommissionInvoices,
  MyTermsAndConditionsPage,
  MyContract,
  Step1,
  Step2,
  Step3,
  Step4,
  Confirmation,
  MySpaces,
  PromoCodes,
  PromoCode,
  PaymentSuccess,
  StaticPageMain,
  TrainingInvoices,
  CalendarTimeTable,
  CalendarGroupsView,
  CalendarUsersView,
  CalendarGoogleSyncView,
  CalendarSynchView,
  MaintenancePage,
  RegisterPage,
  AuthEndpoint,
  ContactInfoPage,
  PromoCodesSeeker,
} from "views";

import BookingPage from "reusableComponents/booking/FullBookingPage";

import {
  PageViewTracker,
  ProtectedRoute,
  ScrollToTop,
  BelgianRouterWrapper
} from "reusableComponents";

import AddSpaceLayout from "layouts/AddSpaceLayout";
import LoginPage from "views/LoginPage";
import { UTMProvider } from "context/UTMContext";
import { SignupForm } from 'modals';


// Initialize EXIF globally BEFORE component renders
if (typeof window !== 'undefined') {
  window.EXIF = EXIF;
  console.log('📸 EXIF initialized:', !!window.EXIF);
}

// Default SEO fallback values
const DEFAULT_SEO = {
  title: "Choose&Work",
  description:
    "Meeting rooms, private offices and coworking spaces. Find your perfect workspace with Choose&Work.",
  keywords: "coworking, meeting rooms, private offices, workspace, Choose&Work",
  canonical: "https://chooseandwork.com",
  ogImage: "/og-image.jpg",
  twitterImage: "/twitter-image.jpg",
};

// Separate component that uses useModal - must be inside ModalProvider
function GoogleSignupHandler() {
  const location = useLocation();
  const { openModal } = useModal();

  useEffect(() => {
    // Check if we need to open signup modal after Google redirect
    const params = new URLSearchParams(location.search);
    if (params.get('signup') === 'true') {
      openModal({
        content: <SignupForm />,
        title: "Complete Registration"
      });
    }
  }, [location, openModal]);

  return null;
}

function App() {
  const [t] = useTranslation("global");
  const user = useSelector(selectCurrentUser);
  const userType = useSelector(selectUserType);
  
  // State to prevent rendering during initial Belgian redirect
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Check if we're on Belgian domain on initial load
  useEffect(() => {
    const isBelgianDomain = window.location.hostname.includes('chooseandwork.be');
    const currentPath = window.location.pathname;
    
    if (isBelgianDomain && !currentPath.startsWith('/fr')) {
      setIsInitializing(true);
      
      // Brief delay to ensure BelgianRouterWrapper can handle the redirect
      const timer = setTimeout(() => {
        setIsInitializing(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Get current path for canonical URL
  const currentPath = window.location.pathname;
  const fullCanonicalUrl = `${DEFAULT_SEO.canonical}${currentPath === "/" ? "" : currentPath}`;

  return (
    <HelmetProvider>
        <UTMProvider>
                <ConsentProvider>

          <LanguageProvider>
            <ModalProvider>
              {/* BelgianRouterWrapper handles all Belgian domain redirects without page reload */}
              <BelgianRouterWrapper>
                {/* Default SEO tags for the entire app */}
                <Helmet>
                  {/* Add robots noindex for maintenance pages */}
                  {(currentPath.includes('/maintenance') || currentPath.includes('/fr/maintenance')) && (
                    <meta name="robots" content="noindex, nofollow" />
                  )}
                  
                  {/* Basic meta tags */}
                  <meta name="description" content={DEFAULT_SEO.description} />
                  <meta name="keywords" content={DEFAULT_SEO.keywords} />

                  {/* Open Graph / Facebook */}
                  <meta property="og:type" content="website" />
                  <meta property="og:url" content={fullCanonicalUrl} />
                  <meta
                    property="og:description"
                    content={DEFAULT_SEO.description}
                  />
                  <meta property="og:image" content={DEFAULT_SEO.ogImage} />
                  <meta property="og:image:width" content="1200" />
                  <meta property="og:image:height" content="630" />

                  {/* Twitter */}
                  <meta name="twitter:card" content="summary_large_image" />
                  <meta name="twitter:url" content={fullCanonicalUrl} />
                  <meta
                    name="twitter:description"
                    content={DEFAULT_SEO.description}
                  />
                  <meta name="twitter:image" content={DEFAULT_SEO.twitterImage} />

                  {/* Canonical */}
                  <link rel="canonical" href={fullCanonicalUrl} />

                  {/* Additional tags */}
                  <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                  />
                  <meta name="theme-color" content="#000000" />
                  <link rel="apple-touch-icon" href="/logo192.png" />
                  <link rel="manifest" href="/manifest.json" />
                </Helmet>

                {/* Add Consent Banner */}
                <ConsentBanner />
                
                <ScrollToTop />
                <GTM />

                {/* Google Signup Handler - must be inside ModalProvider */}
                <GoogleSignupHandler />

                {/* UserDataLayout now contains the maintenance interceptor setup */}
                <UserDataLayout>
                  <Routes>
                    {/* Home */}
                    <Route
                      index
                      element={
                        <Layout transparentHeader>
                          <HomePage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr"
                      element={
                        <Layout transparentHeader>
                          <HomePage />
                        </Layout>
                      }
                    />
                    
                    {/* LoginPage*/}
                    <Route
                      path="/login"
                      element={
                        <Layout transparentHeader>
                          <LoginPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/login"
                      element={
                        <Layout transparentHeader>
                        <LoginPage />
                        </Layout>
                      }
                    />

                    {/* RegisterPage routes */}
                    <Route
                      path="/register/provider"
                      element={
                        <Layout transparentHeader>
                          <RegisterPage userType="provider" />
                        </Layout>
                      }
                    />
                    <Route
                      path="/register/seeker"
                      element={
                        <Layout transparentHeader>
                          <RegisterPage userType="seeker" />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/register/provider"
                      element={
                        <Layout transparentHeader>
                          <RegisterPage userType="provider" />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/register/seeker"
                      element={
                        <Layout transparentHeader>
                          <RegisterPage userType="seeker" />
                        </Layout>
                      }
                    />

                    {/* MaintenancePage - Simple routes */}
                    <Route
                      path="/maintenance"
                      element={
                        <MaintenancePage />
                      }
                    />
                    <Route
                      path="/fr/maintenance"
                      element={
                        <MaintenancePage />
                      }
                    />
                    
                    {/* Search & Space Pages */}
                    <Route
                      path="/search"
                      element={
                        <Layout>
                          <SpacesListPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/search"
                      element={
                        <Layout>
                          <SpacesListPage />
                        </Layout>
                      }
                    />
                    
                    {/* Space Page */}
                    <Route
                      path="/space/:spacePageId"
                      element={
                        window.location.search.includes("externalID") ? (
                          <SpacePage embedded={true} />
                        ) : (
                          <Layout>
                            <SpacePage />
                          </Layout>
                        )
                      }
                    />


                    <Route
                      path="/fr/space/:spacePageId"
                      element={
                        window.location.search.includes("externalID") ? (
                          <SpacePage embedded={true} />
                        ) : (
                          <Layout>
                            <SpacePage />
                          </Layout>
                        )
                      }
                    />
                    <Route
                      path="/space/:themeColor/:spacePageId"
                      element={
                        window.location.search.includes("externalID") ? (
                          <SpacePage forMemberPlus embedded={true} />
                        ) : (
                          <SpacePage forMemberPlus />
                        )
                      }
                    />
                    <Route
                      path="/fr/space/:themeColor/:spacePageId"
                      element={
                        window.location.search.includes("externalID") ? (
                          <SpacePage forMemberPlus embedded={true} />
                        ) : (
                          <SpacePage forMemberPlus />
                        )
                      }
                    />
                    {/* Booking Page - ADD THIS HERE */}
<Route
  path="/space/:spaceId/book"
  element={
    <Layout transparentHeader={false}>
      <BookingPage />
    </Layout>
  }
/>
<Route
  path="/fr/space/:spaceId/book"
  element={
    <Layout transparentHeader={false}>
      <BookingPage />
    </Layout>
  }
/>
                    <Route
                      path="/:customPageID"
                      element={
                        <Layout>
                          <CustomPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/:customPageID"
                      element={
                        <Layout>
                          <CustomPage />
                        </Layout>
                      }
                    />
                    {/* Static Pages */}
                    <Route
                      path="static/:pageKey"
                      element={
                        <Layout>
                          <StaticPageMain />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/static/:pageKey"
                      element={
                        <Layout>
                          <StaticPageMain />
                        </Layout>
                      }
                    />
                    {/* Contact & FAQ */}
                    <Route
                      path="/contact"
                      element={
                        <Layout transparentHeader>
                          <ContactOrganiserPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/contact"
                      element={
                        <Layout transparentHeader>
                          <ContactOrganiserPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/static/contact"
                      element={<Navigate to="/contact-organiser-evenement" replace />}
                    />
                    <Route
                      path="/fr/static/contact"
                      element={<Navigate to="/contact-organiser-evenement" replace />}
                    />
                    <Route
                      path="/static/contact-organiser-evenement"
                      element={<Navigate to="/contact-organiser-evenement" replace />}
                    />
                    <Route
                      path="/fr/static/contact"
                      element={<Navigate to="/contact-organiser-evenement" replace />}
                    />

                    <Route
                      path="/contact-organiser-evenement"
                      element={
                        <Layout transparentHeader>
                          <ContactOrganiserPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/contact-organiser-evenement"
                      element={
                        <Layout transparentHeader>
                          <ContactOrganiserPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/faq"
                      element={
                        <Layout>
                          <FaqPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/faq"
                      element={
                        <Layout>
                          <FaqPage />
                        </Layout>
                      }
                    />
                    {/* Other Pages */}
                    <Route
                      path="/searches"
                      element={
                        <Layout>
                          <SearchesPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/searches"
                      element={
                        <Layout>
                          <SearchesPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/basket"
                      element={
                        <Layout>
                          <BasketPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/basket"
                      element={
                        <Layout>
                          <BasketPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/favorites"
                      element={
                        <Layout>
                          <FavoritesPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/favorites"
                      element={
                        <Layout>
                          <FavoritesPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/landing/:landingPageId"
                      element={<LandingPage />}
                    />
                    <Route
                      path="/fr/landing/:landingPageId"
                      element={<LandingPage />}
                    />
                    <Route
                      path="/resetpwd/:email/:token"
                      element={
                        <Layout>
                          <HomePage />
                          <ResetPasswordPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/resetpwd/:email/:token"
                      element={
                        <Layout>
                          <HomePage />
                          <ResetPasswordPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/payment-result"
                      element={
                        <Layout>
                          <PaymentSuccess />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/payment-result"
                      element={
                        <Layout>
                          <PaymentSuccess />
                        </Layout>
                      }
                    />
                    <Route
                      path="/notfound"
                      element={
                        <Layout>
                          <NotFoundPage />
                        </Layout>
                      }
                    />
                    <Route
                      path="/fr/notfound"
                      element={
                        <Layout>
                          <NotFoundPage />
                        </Layout>
                      }
                    />
                    {/* Dashboard Routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <Layout>
                          <DashboardLayout />
                        </Layout>
                      }
                    >
                      {/* All Authenticated Users */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="home" element={<DashboardMainPage />} />
                        <Route path="messages" element={<MessagesPage />} />
                        <Route
                          path="access"
                          element={<PasswordAndEmailEditingPage />}
                        />
                        <Route path="bookings" element={<BookingsListPage />} />
                        <Route path="history" element={<BookingsHistory />} />
                        <Route
                          path="bookinginvoices"
                          element={<BookingInvoices />}
                        />
                        <Route
                          path="contact"
                          element={
                          <ContactInfoPage/>
                          }
                        />
                         
                      </Route>

                      {/* Seeker-only routes */}
                      <Route
                        element={<ProtectedRoute allowedUserTypes={["seeker"]} />}
                      >
                                                <Route path="promo-codes-seeker" element={<PromoCodesSeeker />} />
                        <Route path="cards" element={<PaymentMethods />} />
                        <Route path="ratings" element={<RatingsPage />} />
                        <Route
                          path="traininginvoices"
                          element={<TrainingInvoices />}
                        />
                        <Route
                          path="favorites"
                          element={<FavoritesPage forDashboard />}
                        />
                        <Route index element={<DashboardMainPage />} />
                      </Route>

                      {/* Provider-only routes */}
                      <Route
                        element={<ProtectedRoute allowedUserTypes={["provider"]} />}
                      >
                        <Route path="invoices" element={<CommissionInvoices />} />
                        <Route path="cgv" element={<MyTermsAndConditionsPage />} />
                        <Route path="contract" element={<MyContract />} />
                        <Route path="spaces" element={<MySpaces />} />
                        <Route path="groups" element={<CalendarGroupsView />} />

                        {user?.promo_permission && (
                          <>
                            <Route path="promo-codes" element={<PromoCodes />} />
                            <Route
                              path="promo-code/create"
                              element={<PromoCode />}
                            />
                            <Route
                              path="promo-code/:promoCodeId"
                              element={<PromoCode />}
                            />
                          </>
                        )}
                      </Route>
                    </Route>
                    
                    {/* Dashboard Routes with /fr prefix */}
                    <Route
                      path="/fr/dashboard"
                      element={
                        <Layout>
                          <DashboardLayout />
                        </Layout>
                      }
                    >
                      {/* All Authenticated Users */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="home" element={<DashboardMainPage />} />
                        <Route path="messages" element={<MessagesPage />} />
                        <Route
                          path="access"
                          element={<PasswordAndEmailEditingPage />}
                        />
                        <Route path="bookings" element={<BookingsListPage />} />
                        <Route path="history" element={<BookingsHistory />} />
                        <Route
                          path="bookinginvoices"
                          element={<BookingInvoices />}
                        />
                        <Route
                          path="contact"
                          element={
                          <ContactInfoPage/>
                          }
                        />
                     
                          
                      </Route>

                      {/* Seeker-only routes */}
                      <Route
                        element={<ProtectedRoute allowedUserTypes={["seeker"]} />}
                      >
                        <Route path="promo-codes-seeker" element={<PromoCodesSeeker />} />
                        <Route path="cards" element={<PaymentMethods />} />
                        <Route path="ratings" element={<RatingsPage />} />
                        <Route
                          path="traininginvoices"
                          element={<TrainingInvoices />}
                        />
                        <Route
                          path="favorites"
                          element={<FavoritesPage forDashboard />}
                        />
                        <Route index element={<DashboardMainPage />} />
                      </Route>

                      {/* Provider-only routes */}
                      <Route
                        element={<ProtectedRoute allowedUserTypes={["provider"]} />}
                      >
                        <Route path="invoices" element={<CommissionInvoices />} />
                        <Route path="cgv" element={<MyTermsAndConditionsPage />} />
                        <Route path="contract" element={<MyContract />} />
                        <Route path="spaces" element={<MySpaces />} />
                        <Route path="groups" element={<CalendarGroupsView />} />

                        {user?.promo_permission && (
                          <>
                            <Route path="promo-codes" element={<PromoCodes />} />
                            <Route
                              path="promo-code/create"
                              element={<PromoCode />}
                            />
                            <Route
                              path="promo-code/:promoCodeId"
                              element={<PromoCode />}
                            />
                          </>
                        )}
                      </Route>
                    </Route>
                    
                    <Route path = "/auth/auth-endpoint" element={<AuthEndpoint/>} />
                    <Route path = "/fr/auth/auth-endpoint" element={<AuthEndpoint/>} />
                    {/* Catch-all */}
                    <Route
                      path="*"
                      element={
                        <Layout>
                          <NotFoundPage />
                        </Layout>
                      }
                    />
                    {/* Calendar Routes (Provider Only) */}
                    <Route
                      element={<ProtectedRoute allowedUserTypes={["provider"]} />}
                    >
                      <Route path="/calendar" element={<Layout />}>
                        <Route element={<CalendarLayout />}>
                          <Route index element={<CalendarTimeTable />} />
                          <Route path="timetable" element={<CalendarTimeTable />} />
                          <Route path="spaces" element={<MySpaces />} />
                          <Route path="groups" element={<CalendarGroupsView />} />
                          <Route path="users" element={<CalendarUsersView />} />
                          <Route path="sync" element={<CalendarSynchView />} />
                          <Route
                            path="syncgoogle"
                            element={<CalendarGoogleSyncView />}
                          />
                        </Route>
                      </Route>
                      
                      <Route path="/fr/calendar" element={<Layout />}>
                        <Route element={<CalendarLayout />}>
                          <Route index element={<CalendarTimeTable />} />
                          <Route path="timetable" element={<CalendarTimeTable />} />
                          <Route path="spaces" element={<MySpaces />} />
                          <Route path="groups" element={<CalendarGroupsView />} />
                          <Route path="users" element={<CalendarUsersView />} />
                          <Route path="sync" element={<CalendarSynchView />} />
                          <Route
                            path="syncgoogle"
                            element={<CalendarGoogleSyncView />}
                          />
                        </Route>
                      </Route>
                    </Route>
                    {/* Create Space Flow (Provider Only) */}
                    <Route
                      element={<ProtectedRoute allowedUserTypes={["provider"]} />}
                    >
                      <Route path="/create-space" element={<Layout />}>
                        <Route element={<AddSpaceLayout />}>
                          <Route index element={<Navigate to="step1" />} />
                          <Route path="step1" element={<Step1 />} />
                          <Route path="step1/:spaceId" element={<Step1 />} />
                          <Route path="step2/:spaceId" element={<Step2 />} />
                          <Route path="step3/:spaceId" element={<Step3 />} />
                          <Route path="step4/:spaceId" element={<Step4 />} />
                          <Route
                            path="confirmation/:spaceId"
                            element={<Confirmation />}
                          />
                        </Route>
                      </Route>
                      
                      <Route path="/fr/create-space" element={<Layout />}>
                        <Route element={<AddSpaceLayout />}>
                          <Route index element={<Navigate to="step1" />} />
                          <Route path="step1" element={<Step1 />} />
                          <Route path="step1/:spaceId" element={<Step1 />} />
                          <Route path="step2/:spaceId" element={<Step2 />} />
                          <Route path="step3/:spaceId" element={<Step3 />} />
                          <Route path="step4/:spaceId" element={<Step4 />} />
                          <Route
                            path="confirmation/:spaceId"
                            element={<Confirmation />}
                          />
                        </Route>
                      </Route>
                    </Route>
                  </Routes>
                </UserDataLayout>
              </BelgianRouterWrapper>
            </ModalProvider>
          </LanguageProvider>
      </ConsentProvider>
              </UTMProvider>
    </HelmetProvider>
  );
}

export default App;